import { DestroyRef, inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { LoginResponse } from '../models/login-response.model';

/**
 * Name of the same-origin channel used to hand a live session to a new tab.
 * `BroadcastChannel` is scoped to the origin AND the browser profile, so a message
 * posted here can never reach another origin — notably not the `dr-view` app, which
 * runs on a different port locally and must never hold a portal session.
 */
const CHANNEL_NAME = 'ep-session-handoff';

/** Default bounded wait for a sibling tab to answer, in milliseconds. */
const DEFAULT_TIMEOUT_MS = 300;

/** A new tab asking whether any sibling holds a live session for `tenant`. */
interface SessionRequest {
  type: 'session-request';
  /** Customer the requesting tab is navigating to; a sibling answers only on a match. */
  tenant: string;
  /** Correlates an offer with this request so a late reply cannot be adopted. */
  nonce: string;
}

/** A sibling tab offering its session in response to a matching request. */
interface SessionOffer {
  type: 'session-offer';
  nonce: string;
  session: LoginResponse;
}

/**
 * An untrusted inbound message after its `type` has been recognised but before its payload is
 * trusted. Deliberately loose: every other field is optional so each handler validates only what
 * it actually needs, and a message missing a field it requires is simply ignored.
 */
interface ParsedMessage {
  type: SessionRequest['type'] | SessionOffer['type'];
  tenant?: string;
  nonce?: string;
  session?: LoginResponse;
}

/**
 * Hands a live Employee Portal session from an existing tab to a newly opened one.
 *
 * ## Why this exists
 *
 * Tokens live in `sessionStorage`, which browsers scope to a single **browsing context**
 * (`auth.service.ts` — "cleared when the browser tab is closed"). A tab created by an external
 * application (Outlook, Teams, Slack) or by pasting a URL therefore starts with an empty store,
 * so `authGuard` correctly finds no token and routes to login — even though the employee is
 * signed in in another tab and nothing has timed out. That is the 98898 *Authenticated Access*
 * defect, and it is a property of the storage model, not of the redirect bridge.
 *
 * This service closes the gap **without moving the credential anywhere**: the new tab asks its
 * siblings for the session and adopts the reply. Nothing new is persisted, no cookie is
 * introduced, and the "cleared on tab close" property survives intact.
 *
 * ## Contract
 *
 * - The request is issued **only** from `authGuard`'s unauthenticated branch — the one path whose
 *   current outcome is already a login page. No navigation that works today is affected.
 * - The **holder** decides, not the requester: {@link AuthService.describeSessionFor} answers only
 *   for a live session whose `customerId` claim matches the requested tenant, and stays **silent**
 *   otherwise. A tab signed in to another customer simply does not reply, so today's multi-tenant
 *   behaviour is preserved exactly.
 * - There is deliberately **no `localStorage` fallback**. A storage-based exchange would write the
 *   token, however briefly, to a store that outlives the tab — reintroducing the very property
 *   this design exists to preserve. Where `BroadcastChannel` is unavailable the caller simply gets
 *   `null` and the existing login path runs.
 * - No leader election or refresh lock is needed: the EP API's refresh is stateless (no refresh
 *   token store, no rotation enforcement), so a superseded refresh token still validates and two
 *   tabs cannot evict one another. **Revisit if the API ever gains a refresh-token store.**
 */
@Injectable({ providedIn: 'root' })
export class SessionHandoffService {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  /** `null` when the browser does not support `BroadcastChannel`; the service then no-ops. */
  private readonly channel: BroadcastChannel | null;

  constructor() {
    this.channel = SessionHandoffService._openChannel();
    if (!this.channel) return;

    // Listening from the constructor is what makes every tab that injects this service a live
    // responder — not only the tabs that need to request. `authGuard` injects it unconditionally
    // for exactly this reason.
    this.channel.addEventListener('message', this._onMessage);
    this.destroyRef.onDestroy(() => {
      this.channel?.removeEventListener('message', this._onMessage);
      this.channel?.close();
    });
  }

  /**
   * Asks sibling tabs of this origin for a live session for `tenant`.
   *
   * @param tenant Customer the requesting tab is navigating to. A sibling replies only on a match.
   * @param timeoutMs Bounded wait before giving up. Paid only on the path that would otherwise
   *   have shown a login page, so it never slows a working navigation.
   * @returns The offered session, or `null` when no sibling answers in time — in which case the
   *   caller must fall through to its normal login path.
   */
  requestSession(tenant: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<LoginResponse | null> {
    const channel = this.channel;
    if (!channel || !tenant) {
      return Promise.resolve(null);
    }

    const nonce = SessionHandoffService._nonce();

    return new Promise<LoginResponse | null>((resolve) => {
      let settled = false;
      let timerId: ReturnType<typeof setTimeout> | null = null;

      // Settling detaches the listener and clears the timer, so an offer arriving after the
      // timeout is ignored rather than resolving a promise the caller has already acted on.
      const settle = (session: LoginResponse | null) => {
        if (settled) return;
        settled = true;
        if (timerId !== null) clearTimeout(timerId);
        channel.removeEventListener('message', onOffer);
        resolve(session);
      };

      const onOffer = (event: MessageEvent) => {
        const message = SessionHandoffService._parse(event.data);
        if (message?.type !== 'session-offer' || message.nonce !== nonce) return;
        settle(message.session ?? null);
      };

      channel.addEventListener('message', onOffer);
      timerId = setTimeout(() => settle(null), timeoutMs);

      try {
        channel.postMessage({ type: 'session-request', tenant, nonce } satisfies SessionRequest);
      } catch {
        // Channel closed between construction and use — behave as if nobody answered.
        settle(null);
      }
    });
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  /**
   * Responder. Answers a sibling's request when — and only when — this tab holds a live session
   * for the requested tenant. Silence is the refusal: no negative reply is ever posted, so the
   * protocol reveals nothing about which tenants are signed in.
   */
  private readonly _onMessage = (event: MessageEvent): void => {
    const message = SessionHandoffService._parse(event.data);
    if (message?.type !== 'session-request' || !message.tenant || !message.nonce) return;

    const session = this.authService.describeSessionFor(message.tenant);
    if (!session) return;

    try {
      this.channel?.postMessage({
        type: 'session-offer',
        nonce: message.nonce,
        session,
      } satisfies SessionOffer);
    } catch {
      // Nothing useful to do — the requester falls through to login on timeout.
    }
  };

  /** Narrows an untrusted `MessageEvent.data` to a known message, or `null` for anything else. */
  private static _parse(data: unknown): ParsedMessage | null {
    if (!data || typeof data !== 'object') return null;
    const candidate = data as { type?: unknown };
    if (candidate.type !== 'session-request' && candidate.type !== 'session-offer') return null;
    return data as ParsedMessage;
  }

  private static _openChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === 'undefined') return null;
    try {
      return new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return null;
    }
  }

  private static _nonce(): string {
    // Deliberately `Math.random`, NOT `crypto.randomUUID()`. This runs on the pre-login guard
    // path, and `_nonce()` is called synchronously by `requestSession()` before the Promise is
    // constructed, so anything that throws here breaks the navigation outright rather than
    // degrading to the login page. Swapping in `crypto.randomUUID()` for SonarQube S2245
    // coincided with the cross-tab hand-off failing in dev2, so it is reverted here.
    // The id is a correlation token, never a credential: it is opaque to both ends and only
    // ever compared for equality, and the channel is already origin- and profile-scoped.
    // If S2245 is revisited, prefer `crypto.getRandomValues` (no secure-context requirement,
    // far wider support) behind a try/catch, and validate it in dev2 before shipping.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
